import { useState, useCallback } from "react";

type SelectOption = { label: string; value: string };

export type CustomCategoryItem = {
    item_name: string;
    item_id: string | null;
    percentage: string;
};

export type CustomCategory = {
    category_name: string;
    category_id: string | null;
    items: CustomCategoryItem[];
};

type ValidationErrors = Record<string, string>;

const emptyItem = (): CustomCategoryItem => ({
    item_name: "",
    item_id: null,
    percentage: "",
});

const emptyCategory = (): CustomCategory => ({
    category_name: "",
    category_id: null,
    items: [emptyItem()],
});

const computeSumError = (items: CustomCategoryItem[]) => {
    const percentages = items
        .map((item) => parseFloat(item.percentage) || 0)
        .filter((p) => p > 0);
    const total = percentages.reduce((acc, p) => acc + p, 0);
    return {
        sum: total,
        sumError:
            percentages.length > 0 && total !== 100
                ? "Percentages must sum to 100"
                : null,
    };
};

export type UseCustomCategoriesReturn = {
    categories: CustomCategory[];
    setCategories: React.Dispatch<React.SetStateAction<CustomCategory[]>>;
    addCategory: () => void;
    removeCategory: (catIndex: number) => void;
    updateCategoryName: (
        catIndex: number,
        option: SelectOption | string | null
    ) => void;
    addItem: (catIndex: number) => void;
    removeItem: (catIndex: number, itemIndex: number) => void;
    updateItemName: (
        catIndex: number,
        itemIndex: number,
        option: SelectOption | string | null
    ) => void;
    updateItemPercentage: (
        catIndex: number,
        itemIndex: number,
        value: string
    ) => void;
    getSumError: (catIndex: number) => string | null;
    getValidationErrors: () => ValidationErrors;
    reset: () => void;
};

export const useCustomCategories = (): UseCustomCategoriesReturn => {
    const [categories, setCategories] = useState<CustomCategory[]>([]);

    const addCategory = useCallback(() => {
        setCategories((prev) => [...prev, emptyCategory()]);
    }, []);

    const removeCategory = useCallback((catIndex: number) => {
        setCategories((prev) => prev.filter((_, i) => i !== catIndex));
    }, []);

    const updateCategoryName = useCallback(
        (catIndex: number, option: SelectOption | string | null) => {
            setCategories((prev) => {
                const updated = [...prev];
                if (option === null) {
                    updated[catIndex] = {
                        ...updated[catIndex],
                        category_name: "",
                        category_id: null,
                        items: [emptyItem()],
                    };
                } else if (typeof option === "string") {
                    updated[catIndex] = {
                        ...updated[catIndex],
                        category_name: option,
                        category_id: null,
                        items: updated[catIndex].items,
                    };
                } else {
                    updated[catIndex] = {
                        ...updated[catIndex],
                        category_name: option.label,
                        category_id: option.value,
                        items: [emptyItem()],
                    };
                }
                return updated;
            });
        },
        []
    );

    const addItem = useCallback((catIndex: number) => {
        setCategories((prev) => {
            const updated = [...prev];
            updated[catIndex] = {
                ...updated[catIndex],
                items: [...updated[catIndex].items, emptyItem()],
            };
            return updated;
        });
    }, []);

    const removeItem = useCallback((catIndex: number, itemIndex: number) => {
        setCategories((prev) => {
            const updated = [...prev];
            const newItems = updated[catIndex].items.filter(
                (_, i) => i !== itemIndex
            );
            updated[catIndex] = {
                ...updated[catIndex],
                items: newItems.length > 0 ? newItems : [emptyItem()],
            };
            return updated;
        });
    }, []);

    const updateItemName = useCallback(
        (
            catIndex: number,
            itemIndex: number,
            option: SelectOption | string | null
        ) => {
            setCategories((prev) => {
                const updated = [...prev];
                const items = [...updated[catIndex].items];
                if (option === null) {
                    items[itemIndex] = {
                        ...items[itemIndex],
                        item_name: "",
                        item_id: null,
                    };
                } else if (typeof option === "string") {
                    items[itemIndex] = {
                        ...items[itemIndex],
                        item_name: option,
                        item_id: null,
                    };
                } else {
                    items[itemIndex] = {
                        ...items[itemIndex],
                        item_name: option.label,
                        item_id: option.value,
                    };
                }
                updated[catIndex] = { ...updated[catIndex], items };
                return updated;
            });
        },
        []
    );

    const updateItemPercentage = useCallback(
        (catIndex: number, itemIndex: number, value: string) => {
            setCategories((prev) => {
                const updated = [...prev];
                const items = [...updated[catIndex].items];
                items[itemIndex] = { ...items[itemIndex], percentage: value };
                updated[catIndex] = { ...updated[catIndex], items };
                return updated;
            });
        },
        []
    );

    const getSumError = useCallback(
        (catIndex: number): string | null => {
            if (catIndex >= categories.length) return null;
            return computeSumError(categories[catIndex].items).sumError;
        },
        [categories]
    );

    const getValidationErrors = useCallback((): ValidationErrors => {
        const errors: ValidationErrors = {};

        const categoryNames = categories
            .map((cat) => cat.category_name.trim().toLowerCase())
            .filter(Boolean);

        categories.forEach((cat, catIndex) => {
            if (!cat.category_name) {
                const hasAnyItem = cat.items.some(
                    (item) => item.item_name || item.percentage
                );
                if (hasAnyItem) {
                    errors[`custom_cat_name_${catIndex}`] =
                        "Category name is required";
                }
                return;
            }

            const normalizedName = cat.category_name.trim().toLowerCase();
            const isDuplicateCategory =
                categoryNames.indexOf(normalizedName) !== catIndex;
            if (isDuplicateCategory) {
                errors[`custom_cat_name_${catIndex}`] =
                    "Category name must be unique";
            }

            const itemNames = cat.items
                .map((item) => item.item_name.trim().toLowerCase())
                .filter(Boolean);

            cat.items.forEach((item, itemIndex) => {
                const hasName = !!item.item_name;
                const hasPercentage = !!item.percentage;
                if (hasName && !hasPercentage) {
                    errors[
                        `custom_cat_${catIndex}_item_percentage_${itemIndex}`
                    ] = "Percentage is required";
                }
                if (hasPercentage && !hasName) {
                    errors[`custom_cat_${catIndex}_item_name_${itemIndex}`] =
                        "Item name is required";
                }
                if (hasName) {
                    const normalizedItemName = item.item_name.trim().toLowerCase();
                    const isDuplicateItem =
                        itemNames.indexOf(normalizedItemName) !== itemIndex;
                    if (isDuplicateItem) {
                        errors[`custom_cat_${catIndex}_item_name_${itemIndex}`] =
                            "Item name must be unique within a category";
                    }
                }
            });

            const { sumError } = computeSumError(cat.items);
            if (sumError) {
                errors[`custom_cat_${catIndex}_sum`] = sumError;
            }
        });
        return errors;
    }, [categories]);

    const reset = useCallback(() => {
        setCategories([]);
    }, []);

    return {
        categories,
        setCategories,
        addCategory,
        removeCategory,
        updateCategoryName,
        addItem,
        removeItem,
        updateItemName,
        updateItemPercentage,
        getSumError,
        getValidationErrors,
        reset,
    };
};
